import PrimaryButton from "@/components/ui/PrimaryButton";
import { isExpectedAuthLinkError } from "@/utils/errors";
import { reportError } from "@/utils/reportError";
import { supabase } from "@/utils/supabase";
import type { AuthError, EmailOtpType } from "@supabase/supabase-js";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

const verifyOtpOnce = new Map<string, Promise<{ error: AuthError | null }>>();

function verifyEmailOtpOnce(tokenHash: string, type: EmailOtpType) {
  const key = `${type}:${tokenHash}`;
  const existing = verifyOtpOnce.get(key);
  if (existing) return existing;
  const request = supabase.auth.verifyOtp({ token_hash: tokenHash, type });
  verifyOtpOnce.set(key, request);
  return request;
}

// Écran atteint quand l'app est ouverte via l'App Link / Universal Link
// https://www.repaschallenge.ch/confirmation ou le deep link
// repas://confirmation (bouton « Ouvrir l'app » des pages web).
//
// L'email de confirmation porte désormais ?token_hash=…&type=signup et c'est
// le CLIENT qui valide (verifyOtp), plus un GET sur /auth/v1/verify : ce
// dernier était consommé par les scanners de liens des messageries
// d'entreprise, qui confirmaient le compte à la place de l'utilisateur — lequel
// tombait ensuite sur une erreur alors qu'il pouvait se connecter. Même
// mécanique que la réinitialisation de mot de passe (commit 48c28f5).
//
// Comme l'App Link Android intercepte cette URL, la validation DOIT exister
// ici : sinon l'app afficherait « Adresse confirmée » sans rien valider.
// Le lien sans token (repas://confirmation) reste valide : il arrive après une
// validation déjà faite sur la page web.
type Status = "verifying" | "success" | "error";

/** expo-router renvoie string | string[] : on ne garde que la 1re valeur. */
function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export default function ConfirmationScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useLocalSearchParams<{ token_hash?: string; type?: string }>();
  const tokenHash = firstParam(params.token_hash);
  const otpType = firstParam(params.type);

  const [status, setStatus] = useState<Status>(
    tokenHash ? "verifying" : "success"
  );
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Lien de confirmation : on valide nous-mêmes le token. verifyOtp ouvre
      // une session en cas de succès — l'utilisateur n'a alors plus à saisir
      // ses identifiants.
      if (tokenHash) {
        // `type` vient du lien : 'signup' à l'inscription, 'email_change' quand
        // l'utilisateur change d'adresse depuis son profil (même page, même
        // App Link Android). Le cast en "signup" était trompeur.
        const { error } = await verifyEmailOtpOnce(
          tokenHash,
          (otpType as EmailOtpType) || "signup",
        );
        if (cancelled) return;
        if (error) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            router.replace("/(tabs)");
            return;
          }
          if (!isExpectedAuthLinkError(error)) reportError(error);
          setStatus("error");
          setChecking(false);
          return;
        }
        setStatus("success");
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session) {
        router.replace("/(tabs)");
      } else {
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [router, tokenHash, otpType]);

  if (status === "verifying") {
    return (
      <View style={styles.container}>
        <StatusBar style="dark" />
        <LinearGradient
          colors={["#FBEFE3", "#FFFFFF"]}
          locations={[0, 0.65]}
          style={styles.background}
        >
          <View style={styles.content}>
            <ActivityIndicator size="large" color="#1C453F" />
            <Text style={styles.subtitle}>{t("confirmation.verifying")}</Text>
          </View>
        </LinearGradient>
      </View>
    );
  }

  const isError = status === "error";

  return (
    <View style={styles.container}>
      <StatusBar style="dark" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.65]}
        style={styles.background}
      >
        <View style={styles.content}>
          <Ionicons
            name={isError ? "alert-circle" : "checkmark-circle"}
            size={72}
            color={isError ? "#CD7728" : "#1C453F"}
          />
          <Text style={styles.title}>
            {isError ? t("confirmation.errorTitle") : t("confirmation.title")}
          </Text>
          <Text style={styles.subtitle}>
            {isError ? t("confirmation.errorText") : t("confirmation.subtitle")}
          </Text>
          {!checking && (
            <View style={styles.buttonWrap}>
              <PrimaryButton
                title={t("confirmation.loginButton")}
                onPress={() => router.replace("/login")}
              />
            </View>
          )}
        </View>
      </LinearGradient>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  background: { flex: 1 },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 32,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 26,
    color: "#000",
    marginTop: 20,
    marginBottom: 10,
    textAlign: "center",
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    textAlign: "center",
  },
  buttonWrap: {
    marginTop: 32,
    alignSelf: "stretch",
  },
});
